import { Mouthpiece, MouthpieceFeatureList, MouthpiecesWrapperBlock } from "@/app/parts/product-detail/MouthpiecesWrapper";
import productDetail from "@/styles/modules/product_detail.module.scss";
import * as Api from "@api";
import * as C from "@components";
import React from "react";
import * as P from "@/parts";

export const MouthpiecesContentKeys = [
    "mouthpieces.headline",
    "mouthpieces.feature.1",
    "mouthpieces.feature.1.pretitle",
    "mouthpieces.feature.2",
    "mouthpieces.feature.2.pretitle",
    // Footnote under the grid explaining the "*" suffix on custom-only mouthpiece titles.
    // Optional: products that have not been seeded with it simply render no footnote.
    ["mouthpieces.footer"],
] as const;

const enum MouthpieceCategory
{
    Ceramic = "Ceramic",
    Hemp = "Hemp",
    Plastic = "Plastic",
    Custom = "Custom",
}

interface MouthpieceDescriptor
{
    category: MouthpieceCategory | MouthpieceCategory[];
    title: string;
    order: number;
}

const MouthpiecesDescriptors = {
    "black-snaptix": {
        category: [MouthpieceCategory.Custom, MouthpieceCategory.Plastic],
        title: "Black Plastic Snaptix*",
        order: 0,
    },
    "white-snaptix": {
        category: [MouthpieceCategory.Custom, MouthpieceCategory.Plastic],
        title: "White Plastic Snaptix*",
        order: 0,
    },
    "ceramic-barrel-black": {
        category: MouthpieceCategory.Ceramic,
        title: "Ceramic Barrel Black",
        order: 5,
    },
    "ceramic-barrel-white": {
        category: MouthpieceCategory.Ceramic,
        title: "Ceramic Barrel White",
        order: 6,
    },
    "ceramic-bullet-black": {
        category: [MouthpieceCategory.Ceramic, MouthpieceCategory.Custom],
        title: "Ceramic Bullet Black*",
        order: 7,
    },
    "ceramic-bullet-white": {
        category: MouthpieceCategory.Ceramic,
        title: "Ceramic Bullet White",
        order: 8,
    },
    "ceramic-flat-black": {
        category: MouthpieceCategory.Ceramic,
        title: "Ceramic Flat Black",
        order: 0,
    },
    "ceramic-flat-white": {
        category: MouthpieceCategory.Ceramic,
        title: "Ceramic Flat White",
        order: 1,
    },
    "hemp-barrel-natural": {
        category: MouthpieceCategory.Hemp,
        title: "Hemp Barrel Natural",
        order: 9,
    },
    "hemp-barrel-white": {
        category: [MouthpieceCategory.Hemp, MouthpieceCategory.Custom],
        title: "Hemp Barrel White*",
        order: 10,
    },
    "hemp-flat-natural": {
        category: MouthpieceCategory.Hemp,
        title: "Hemp Flat Natural",
        order: 2,
    },
    "hemp-flat-white": {
        category: MouthpieceCategory.Hemp,
        title: "Hemp Flat White",
        order: 3,
    },
    "metal-barrel": {
        category: MouthpieceCategory.Custom,
        title: "Metal<br>Barrel*",
        order: 19,
    },
    "metal-flat-silver": {
        category: MouthpieceCategory.Custom,
        title: "Metal Flat<br>Silver*",
        order: 18,
    },
    "plastic-barrel-black": {
        category: MouthpieceCategory.Plastic,
        title: "Plastic Barrel Black",
        order: 11,
    },
    "plastic-barrel-black-vortex": {
        category: MouthpieceCategory.Plastic,
        title: "Plastic Barrel Black Vortex",
        order: 12,
    },
    "plastic-barrel-clear": {
        category: MouthpieceCategory.Plastic,
        title: "Plastic Barrel Clear",
        order: 13,
    },
    "plastic-barrel-clear-vortex": {
        category: MouthpieceCategory.Plastic,
        title: "Plastic Barrel Clear Vortex",
        order: 14,
    },
    "plastic-barrel-white": {
        category: MouthpieceCategory.Plastic,
        title: "Plastic Barrel White",
        order: 15,
    },
    "plastic-flat-black": {
        category: MouthpieceCategory.Plastic,
        title: "Plastic Flat Black",
        order: 17,
    },
    "plastic-flat-clear": {
        category: MouthpieceCategory.Plastic,
        title: "Plastic<br>Flat",
        order: 4,
    },
    "plastic-flat-white": {
        category: MouthpieceCategory.Plastic,
        title: "Plastic Flat White",
        order: 16,
    },
    "wood-barrel-natural": {
        category: MouthpieceCategory.Custom,
        title: "Wood Barrel Natural*",
        order: 21,
    },
    "wood-flat-natural": {
        category: MouthpieceCategory.Custom,
        title: "Wood Flat Natural*",
        order: 20,
    },
} as const satisfies Record<string, MouthpieceDescriptor>;

/**
 * Which per-product Snaptix hero renders (`<slug>-<state>.2.png`) each mouthpiece directory ships.
 *   "both"        — both open and closed renders exist; the hover swap animates.
 *   "closed-only" — only the closed render exists; hover reuses it (no swap) rather than 404.
 * Directories not listed fall back to the shared @global render entirely — see snaptixHeroSrc.
 */
const SNAPTIX_RENDER_COVERAGE: Readonly<Record<string, "both" | "closed-only">> = {
    "/images/products/cartridges/active-ezclick-glass-plus/mouthpieces/": "both",
    "/images/products/cartridges/active-ezclick-poly-plus/mouthpieces/": "both",
    "/images/products/cartridges/ezclick-ceramic-pro/mouthpieces/": "both",
    "/images/products/cartridges/ezclick-glass/mouthpieces/": "both",
    "/images/products/cartridges/ezclick-poly/mouthpieces/": "both",
    // All-in-ones ship only the closed render for now; upgrade to "both" once the open
    // (LockTop up) shots land alongside their -open.2.png files.
    "/images/products/all-in-ones/active-glass/mouthpieces/": "closed-only",
    "/images/products/all-in-ones/active-ceramic/mouthpieces/": "closed-only",
};

interface MouthpiecesSectionProps
{
    mouthpiecesDirectory: string;
    pageContent: Api.Content<typeof MouthpiecesContentKeys[number]>;
}

export const MouthpiecesSection = ({ mouthpiecesDirectory, pageContent }: MouthpiecesSectionProps): React.FunctionComponentElement<MouthpiecesSectionProps> => 
{
    const mouthpieceThumbsDirectory = "/images/products/@global/mouthpieces/";

    // Snaptix renders come in an open and a closed state, swapped on hover. The hero normally uses
    // the per-product shot — the mouthpiece fitted to this product's body, suffixed ".2" — but those
    // only exist for the products in SNAPTIX_RENDER_COVERAGE. Anything else falls back to the
    // standalone @global render, which every product has, so the hero never points at a missing file.
    //
    // To add a product: drop the {black,white}-snaptix-{closed,open}.2.png renders into its
    // mouthpieces directory, run `npm run build:images`, then list the directory above.
    const snaptixCoverage = SNAPTIX_RENDER_COVERAGE[mouthpiecesDirectory];

    const snaptixThumbSrc = (key: string) => ({
        open: `${mouthpieceThumbsDirectory}${key}-open.png`,
        closed: `${mouthpieceThumbsDirectory}${key}-closed.png`,
    });

    const snaptixHeroSrc = (key: string) =>
    {
        if (!snaptixCoverage)
        {
            return snaptixThumbSrc(key);
        }

        const closed = `${mouthpiecesDirectory}${key}-closed.2.png`;
        const open = snaptixCoverage === "both" ? `${mouthpiecesDirectory}${key}-open.2.png` : closed;
        return { open, closed };
    };

    const mouthpieces: Mouthpiece[] = Object.entries(MouthpiecesDescriptors).filter(([key]) => !(Api.SnaptixHidden && key.endsWith("-snaptix"))).sort(([, a], [, b]) => a.order - b.order).map(([key, value]) => ({
        imgSrc: `${mouthpiecesDirectory}${key}.png`,
        imgWidth: 384,
        imgHeight: 628,
        imgPreviewSrc: `${mouthpieceThumbsDirectory}${key}.png`,
        imgPreviewWidth: 45,
        imgPreviewHeight: 120,
        caption: value.title,
        category: value.category,
        ...(key.endsWith("-snaptix") ? {
            imgSnaptixSrc: snaptixHeroSrc(key),
            imgPreviewSnaptixSrc: snaptixThumbSrc(key),
        } : {}),
    }));

    const mouthpieceFeatures: MouthpieceFeatureList[] = [
        {
            imgSrc: `${mouthpieceThumbsDirectory}featured-hempzyme.png`,
            imgAlt: 'ACTIVE HempZyme™',
            imgWidth: 169,
            imgHeight: 319,
            content: pageContent["mouthpieces.feature.1"].data,
            pretitle: pageContent["mouthpieces.feature.1.pretitle"]?.data ?? "FEATURED mouthpiece"
        },
        {
            imgSrc: `${mouthpieceThumbsDirectory}featured-vortex.png`,
            imgAlt: 'ACTIVE Vortex™',
            imgWidth: 199,
            imgHeight: 310,
            content: pageContent["mouthpieces.feature.2"].data,
            pretitle: pageContent["mouthpieces.feature.2.pretitle"]?.data ?? "FEATURED mouthpiece"
        }
    ];

    const footer = pageContent["mouthpieces.footer"];

    return (
        <div className={productDetail.product_detail}>
            <MouthpiecesWrapperBlock
                header={pageContent["mouthpieces.headline"].data}
                list={mouthpieces}
                features={[]}
                footer={footer !== undefined ? <C.PageContent {...footer} centered typography={C.Typography.P5Caption} margins={{ top: C.Margin.D30 }} /> : undefined}
            />
        </div>
    );
};